import React, { useContext, useEffect, useState } from "react";
import TabList from "../components/Tabs/TabList";
import TabPanel from "../components/Tabs/TabPanel";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
} from "@mui/material";
import PageHeader from "../components/PageHeader";
import FormInput from "../forms/FormInput";
import { useFormik } from "formik";
import FormDialog from "../forms/FormDialog";
import CustomTable from "../components/Tables/CustomTable";
import { roleTableKeys } from "../data/TableData/role";
import { del, get, post, put } from "../services/apiMethods";
import URLS from "../services/urlConstants";
import { ROLE_PERMISSIONS } from "../data/roleData";
import UserContext from "../context/UserContext";

const data = [
  {
    _id: "1",
    roleName: "abcc",
  },
];

function toggleItemInArray(array = [], item) {
  const index = array?.indexOf(item);

  if (index === -1) {
    // Item not present, add it to the array
    array.push(item);
  } else {
    // Item present, remove it from the array
    array.splice(index, 1);
  }

  return array;
}

function addIfNotPresent(arr, str) {
  if (!arr.includes(str)) {
    arr.push(str);
  }
  return arr;
}

function removeFromArray(arr, str) {
  const index = arr.indexOf(str);
  if (index !== -1) {
    arr.splice(index, 1);
  }
  return arr;
}

const StyledBox = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.spacing(2),

  position: "fixed",
  bottom: 0,
  right: 0,
  left: 0,
  background: "whitesmoke",

  padding: theme.spacing(2),
  zIndex: 1000,
}));

const CRUD_OPERATIONS = ["view_menu", "view", "add", "update", "delete"];

export default function RolePermissions() {
  const { getCurrentUser } = useContext(UserContext);
  const [value, setValue] = React.useState(0);
  const [roles, setRoles] = useState([]);
  const [roleToEdit, setRoleToEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const [roleKeys, setRoleKeys] = useState([]);
  const [readOnly, setReadOnly] = useState(false);
  const [allPermissionUpdate, setAllPermissionUpdate] = useState([
    "view_menu",
    "view",
    "add",
    "update",
    "delete",
  ]);

  const getRoles = async () => {
    try {
      const { data } = await get(URLS.roles.list);
      setRoles(data.result);
    } catch (error) {
      console.log(error);
    }
  };

  const getRoleKeys = async () => {
    try {
      const { data } = await get(URLS.roles.getKeys);

      if (roleToEdit) {
        let allKeys = [...roleToEdit.permissions];
        for (let key of data.result) {
          if (!allKeys.find((k) => k.module == key.module)) {
            allKeys.push(key);
          }
        }
        setRoleKeys(allKeys);
      } else {
        setRoleKeys(data.result);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getRoles();
    getRoleKeys();
    if (value == 0) {
      setRoleToEdit(null);
    }
  }, [value]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleRoleEditClick = (data) => {
    setReadOnly(false);
    setRoleToEdit(data);
    setValue(1);
  };

  const handleShowData = (data) => {
    setReadOnly(true);
    setRoleToEdit(data);
    setValue(1);
  };
  const handleCancelClick = () => {
    setValue(0);
  };
  const openRoleAddUpdateModal = () => {
    setValue(0);
    setOpen(true);
  };

  const handleModalClose = () => setOpen(false);

  const handleAddOrUpdate = async (values) => {
    let payload = {
      ...values,
      permissions: roleKeys,
    };

    if (!roleToEdit) {
      payload["permissions"] = roleKeys.map((r) => ({ ...r, permissions: [] }));
    }

    try {
      if (roleToEdit) {
        const { data } = await put(
          URLS.roles.update + "/" + roleToEdit._id,
          payload
        );
        setValue(0);
        getRoles();
      } else {
        const { data } = await post(URLS.roles.create, payload);
        setValue(0);
        getRoles();
      }

      formik.resetForm();
      setOpen(false);
    } catch (error) {
      console.log(error);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: "",
    },
    onSubmit: handleAddOrUpdate,
    enableReinitialize: true,
  });

  const formikUpdate = useFormik({
    initialValues: {
      name: roleToEdit?.name || "",
    },
    onSubmit: handleAddOrUpdate,
    enableReinitialize: true,
  });

  const handleChangePermission = (module, permission) => {
    setRoleKeys(
      roleKeys.map((k) =>
        k.module === module
          ? {
              ...k,
              permissions: toggleItemInArray(
                k.permissions,
                permission.toLowerCase()
              ),
            }
          : k
      )
    );
  };

  const handleUpdateAll = (permission) => {
    setRoleKeys(
      roleKeys.map((r) => ({
        ...r,
        permissions: allPermissionUpdate.includes(permission)
          ? removeFromArray(r.permissions, permission)
          : addIfNotPresent(r.permissions, permission),
      }))
    );
  };

  useEffect(() => {
    let allPermissions = [];

    for (let co of CRUD_OPERATIONS) {
      if (
        roleKeys.every((module) =>
          module.permissions.includes(co.toLowerCase())
        )
      ) {
        allPermissions.push(co.toLowerCase());
      } else {
        allPermissions.filter((p) => p !== co);
      }
    }

    setAllPermissionUpdate(allPermissions);
  }, [roleKeys]);

  const handleDeleteRole = async (id, reason) => {
    try {
      const { data } = await del(URLS.roles.delete + "/" + id, {
        params: {
          reasonForDelete: reason,
        },
      });
      setRoles(roles.filter((r) => r._id !== id));
    } catch (error) {
      console.log(error);
    }
  };
  return (
    <>
      <PageHeader
        onAddClick={openRoleAddUpdateModal}
        showAddField={false}
        title="Roles and Permission"
        module="Roles and Permission"
      />
      <FormDialog
        open={open}
        onClose={handleModalClose}
        submitButtonTitle={roleToEdit ? "Update" : "Add"}
        formik={formik}
        formTitle={roleToEdit ? "Update Role Name" : "Add Role Name"}
      >
        <Grid container rowSpacing={1} columnSpacing={2}>
          <Grid item xs={12} sm={12} md={12}>
            <FormInput
              required={true}
              name="name"
              type="text"
              formik={formik}
              label="Role Name"
            />
          </Grid>
        </Grid>
      </FormDialog>
      <TabList
        onChange={handleChange}
        value={value}
        labels={
          roleToEdit && value === 1
            ? ["List", readOnly ? "view" : "Edit"]
            : ["List"]
        }
      />
      <TabPanel value={value} index={0}>
        <CustomTable
          actions={["edit", "delete", "view"]}
          bodyDataModal="role"
          bodyData={roles}
          tableKeys={roleTableKeys}
          onEditClick={handleRoleEditClick}
          onDeleteClick={handleDeleteRole}
          onViewClick={handleShowData}
          module="Roles and Permission"
        />
      </TabPanel>

      {roleToEdit && value == 1 && (
        <TabPanel value={value} index={1}>
          <Grid container rowSpacing={1} columnSpacing={2}>
            <Grid item xs={12} sm={12} md={12}>
              <FormInput
                required={true}
                name="name"
                type="text"
                formik={formikUpdate}
                label="Role Name"
                disabled={readOnly}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              <Typography
                sx={{
                  margin: "10px 0px",
                  fontWeight: "500",
                  fontSize: "16px",
                }}
              >
                {" "}
                {readOnly
                  ? "View permission for this role"
                  : "Set permission for this role"}
              </Typography>
            </Grid>
          </Grid>
          <TableContainer
            component={Paper}
            sx={{ marginBottom: "40px", maxHeight: 600 }}
          >
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      color: (theme) =>
                        theme.palette.mode === "dark" ? "white" : "black",
                    }}
                  >
                    Module Name
                  </TableCell>
                  <TableCell
                    sx={{
                      color: (theme) =>
                        theme.palette.mode === "dark" ? "white" : "black",
                    }}
                    align="center"
                  >
                    View Menu
                    <Checkbox
                      onChange={() => handleUpdateAll("view_menu")}
                      disabled={readOnly}
                      name="view_menu"
                      checked={allPermissionUpdate.includes("view_menu")}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      color: (theme) =>
                        theme.palette.mode === "dark" ? "white" : "black",
                    }}
                    align="center"
                  >
                    View
                    <Checkbox
                      onChange={() => handleUpdateAll("view")}
                      disabled={readOnly}
                      name="view"
                      checked={allPermissionUpdate.includes("view")}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      color: (theme) =>
                        theme.palette.mode === "dark" ? "white" : "black",
                    }}
                    align="center"
                  >
                    Add{" "}
                    <Checkbox
                      checked={allPermissionUpdate.includes("add")}
                      onChange={() => handleUpdateAll("add")}
                      disabled={readOnly}
                      name="add"
                    />{" "}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: (theme) =>
                        theme.palette.mode === "dark" ? "white" : "black",
                    }}
                    align="center"
                  >
                    Update{" "}
                    <Checkbox
                      checked={allPermissionUpdate.includes("update")}
                      onChange={() => handleUpdateAll("update")}
                      disabled={readOnly}
                      name="update"
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      color: (theme) =>
                        theme.palette.mode === "dark" ? "white" : "black",
                    }}
                    align="center"
                  >
                    Delete{" "}
                    <Checkbox
                      checked={allPermissionUpdate.includes("delete")}
                      onChange={() => handleUpdateAll("delete")}
                      disabled={readOnly}
                      name="delete"
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ROLE_PERMISSIONS.map((roleModule, i) => {
                  const { module, subModule } = roleModule;

                  return (
                    <>
                      <TableRow key={i + roleModule.module}>
                        <TableCell
                          colSpan={6}
                          sx={{
                            backgroundColor: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(32,33,32,1)"
                                : theme.palette.grey[100],
                          }}
                        >
                          <b>{module}</b>
                        </TableCell>
                      </TableRow>
                      {subModule.map((m, i) => (
                        <TableRow k={m.module + i.toString()}>
                          <TableCell align="center">{m.module}</TableCell>

                          {CRUD_OPERATIONS.map((o) => (
                            <TableCell align="center" key={o}>
                              <Checkbox
                                onChange={() =>
                                  handleChangePermission(m.module, o)
                                }
                                disabled={readOnly}
                                checked={
                                  roleKeys
                                    .find((role) => role.module === m.module)
                                    ?.permissions?.includes(o) || false
                                }
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Grid container>
            <Grid item xs={12} md={12}>
              <StyledBox
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(32,33,32,1)"
                      : theme.palette.grey[100],
                }}
              >
                <Stack spacing={2} direction="row">
                  <Button
                    onClick={handleCancelClick}
                    variant="contained"
                    color="error"
                    size="small"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={formikUpdate.handleSubmit}
                    variant="contained"
                    size="small"
                    disabled={readOnly}
                  >
                    Update
                  </Button>
                </Stack>
              </StyledBox>
            </Grid>
          </Grid>
        </TabPanel>
      )}
    </>
  );
}
